import {
  debounce,
  DialogActions,
  FormControlLabel,
  IconButton,
  TextField,
  Typography
} from '@mui/material';
import React, {
  ReactElement,
  FC,
  ReactNode,
  SyntheticEvent,
  useEffect,
  useState,
  useMemo
} from 'react';
import { IOption, TSlotInfo } from '../Interface/Interface';
import { IOptionLabelFunc } from './DefaultComponent';
import { makeStyles } from 'tss-react/mui';
import { darkBlue, lightgray, white } from '../Constants/Colors';
import InfoIcon from '@mui/icons-material/Info';

import { Box } from '@mui/system';
import * as Showdown from 'showdown';
import CloseIcon from '@mui/icons-material/Close';
import { AbsoluteDialog } from './ContainerPositionProvider';
import { t } from 'react-i18nify';
import { OptionInfo } from './OptionInfo';
import { isEqual } from 'lodash';
import { getClosestAmountForInputValue } from '../Helpers/CPUAmountStepHelpers';
import { MultiselectOptionTextInput } from './MultiselectOptionTextInput';

interface IOptionProps {
  option: IOption;
  onChange: (optionIdentifier: string, updateSkipped?: () => void) => void;
  control: ReactElement;
  optionLabel?: IOptionLabelFunc;
  disabled?: boolean;
  amountSelectable?: boolean;
  minAmount?: number;
  amount: number;
  amountSteps?: number[];
  multiselect?: boolean;
  onAmountChanged: (
    optionIdentifier: string,
    amount: number,
    updateSkipped?: () => void
  ) => void;
  validateOnClick?: (
    optionIdentifier: string,
    updateSkipped?: () => void
  ) => number;
  onInputTextChange?: (optionIdentifier: string, inputText: string) => void;
}

const useStyles = makeStyles<{ disabled: boolean }>()((theme, { disabled }) => {
  return {
    labelGroup: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      overflow: 'hidden',
      justifyContent: 'space-between',
      height: '100%',
      '& > :not(.optionInfo)': {
        opacity: disabled ? 0.6 : 1
      }
    },
    label: {
      paddingLeft: 0,
      paddingRight: 0,
      pointerEvents: 'auto'
    },
    dialog: {
      '& .MuiPaper-root': {
        maxWidth: 500,
        width: 500,
        '@media screen and (max-width: 500px)': {
          width: '80vw'
        }
      }
    },
    dialogContent: {
      padding: '15px !important',
      paddingTop: '0px !important',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'inherit !important'
    },
    infoButton: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column'
    }
  };
});
const converter = new Showdown.Converter({
  tables: true,
  simplifiedAutoLink: true,
  openLinksInNewWindow: true
});

const DefaultOption: FC<IOptionProps> = ({
  option,
  onChange,
  control,
  optionLabel,
  disabled = false,
  amountSelectable = false,
  onAmountChanged,
  amount,
  amountSteps,
  validateOnClick,
  onInputTextChange
}): ReactElement => {
  const { classes } = useStyles({ disabled });
  const [open, setOpen] = React.useState(false);
  const handleClickOpen = (event: SyntheticEvent) => {
    event.preventDefault();
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const { identifier, title, description } = option;

  const [showSlotInfo, setShowSlotInfo] = useState(false);
  const [slotInfo, setSlotInfo] = useState<TSlotInfo | undefined>(undefined);
  useEffect(() => {
    const { slotInfoAdd, slotInfoRemove, slotInfoExceeded } = option;
    if (slotInfoExceeded) {
      if (slotInfo !== slotInfoExceeded) {
        setSlotInfo(slotInfoExceeded);
        setShowSlotInfo(true);
      }
      return;
    }
    const slotInfoMatch =
      !(slotInfo instanceof Array) &&
      [slotInfoAdd, slotInfoRemove].find((item) =>
        isEqual(slotInfo?.slots, item?.slots)
      );
    if (!slotInfoMatch && showSlotInfo) {
      setShowSlotInfo(false);
    }
  }, [option, slotInfo, showSlotInfo]);

  const handleInvalidAction = (invalidSlotInfo?: TSlotInfo) => {
    invalidSlotInfo && setSlotInfo(invalidSlotInfo);
    setShowSlotInfo(true);
    setLocalAmountValue(amount);
  };

  const onFormControlChange = () => {
    if (disabled) {
      return;
    }
    onChange(identifier, handleInvalidAction);
  };

  const [localAmountValue, setLocalAmountValue] = useState(amount);
  useEffect(() => {
    setLocalAmountValue(amount);
  }, [amount]);

  const onAmountChangedDebounced = useMemo(
    () => debounce(onAmountChanged, 1000),
    [onAmountChanged]
  );

  const netPrices = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return (
      !query.has('_display_net_prices') ||
      (query.has('_display_net_prices') &&
        (query.get('_display_net_prices') === '1' ||
          query.get('_display_net_prices') === 'true'))
    );
  }, []);

  const onAmountInputChange = (e: any) => {
    if (disabled) {
      return;
    }
    // if the user tried to clear the input, select the text so a new number can be entered
    if (e.target.value === '') {
      e.target.value = localAmountValue;
      e.target.select();
    } else {
      const minValue = option.minAmount || 0;
      let intValue = parseInt(e.target.value);
      if (amountSteps) {
        intValue = getClosestAmountForInputValue(amountSteps, intValue, amount);
      }
      if (intValue < minValue) {
        intValue = minValue;
        e.target.value = intValue;
      }
      setLocalAmountValue(intValue);
      onAmountChangedDebounced(
        option.identifier,
        intValue,
        handleInvalidAction
      );
    }
  };

  const handleInputTextChange = (inputText: string) => {
    if (disabled) {
      return;
    }
    onInputTextChange && onInputTextChange(option.identifier, inputText);
  };

  const onClickHandler = (evt: React.MouseEvent) => {
    if (
      !validateOnClick ||
      ['input' || 'button'].includes(
        (evt.target as HTMLElement).tagName.toLowerCase()
      )
    ) {
      return;
    }
    const nextAmount = validateOnClick(identifier, handleInvalidAction);
    const minValue = option.minAmount || 0;
    if (
      !amountSelectable &&
      amount === 1 &&
      nextAmount === 0 &&
      nextAmount >= minValue
    ) {
      onChange(identifier, handleInvalidAction);
    }
  };
  const renderLabel = (): ReactNode => {
    return (
      <Box className={classes.labelGroup}>
        <Box className={classes.infoButton}>
          <Typography variant="h4" className={classes.label}>
            {optionLabel ? optionLabel(option) : title}
          </Typography>

          {!showSlotInfo && (
            <Box>
              {description && (
                <>
                  <InfoIcon
                    sx={{ pt: 0.5, color: darkBlue, pointerEvents: 'auto' }}
                    onClick={handleClickOpen}
                  />

                  <AbsoluteDialog
                    open={open}
                    onClose={handleClose}
                    className={classes.dialog}>
                    <IconButton
                      disableRipple
                      sx={{ justifyContent: 'end' }}
                      onClick={handleClose}>
                      <CloseIcon />
                    </IconButton>
                    <DialogActions className={classes.dialogContent}>
                      <Typography sx={{ color: darkBlue }}>{title}</Typography>
                      <Typography
                        variant="h4"
                        dangerouslySetInnerHTML={{
                          __html: converter.makeHtml(description)
                        }}
                      />
                    </DialogActions>
                  </AbsoluteDialog>
                </>
              )}
            </Box>
          )}
        </Box>

        {showSlotInfo ? (
          <OptionInfo
            show={showSlotInfo}
            slotInfo={slotInfo}
            onClose={() => setShowSlotInfo(false)}
          />
        ) : (
          <>
            {amountSelectable && amount > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mt: '12px',
                  gap: '16px'
                }}>
                <TextField
                  inputProps={{ type: 'number' }}
                  sx={{
                    pointerEvents: 'all',
                    maxWidth: '100px',
                    background: white
                  }}
                  value={localAmountValue}
                  size="small"
                  onChange={onAmountInputChange}
                  disabled={disabled}
                />
                {option.maxAmount && (
                  <Typography
                    variant="body2"
                    sx={{
                      display: 'inline-flex',
                      fontStyle: 'italic'
                    }}>{`(${t('maxAmount')}: ${option.maxAmount})`}</Typography>
                )}
                {option.selectedRaidProviderOptions?.length && (
                  <MultiselectOptionTextInput
                    label={t('diskSelectionLabel')}
                    value={option.inputText}
                    onChange={handleInputTextChange}
                    disabled={disabled}
                    options={
                      option.selectedRaidProviderOptions?.map(
                        ({ title }) => title
                      ) || []
                    }
                  />
                )}
              </Box>
            )}

            <Typography
              variant="h4"
              sx={{
                pointerEvents: 'auto',
                fontWeight: 'bold',
                textAlign: 'start',
                marginTop: '16px'
              }}>
              {(
                netPrices
                  ? option.hasNetPrice === false
                  : option.hasPrice === false
              )
                ? t('inStandard')
                : option.priceFormatted}
            </Typography>
          </>
        )}
      </Box>
    );
  };

  return (
    <FormControlLabel
      disabled={disabled}
      disableTypography={true}
      sx={{
        margin: '0px',
        position: 'relative',
        minHeight: '60px',
        alignItems: 'flex-start',
        display: 'flex',
        padding: '16px',
        background: lightgray,
        flex: 1
      }}
      onChange={onFormControlChange}
      value={identifier}
      control={control}
      label={renderLabel()}
      onClick={onClickHandler}
    />
  );
};
export default DefaultOption;
